'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';
import { Table } from '../../components/table';
import { Text } from '../../components/text';
import { Badge } from '../../components/badge';
import { Splitter } from '../../components/splitter';
import { HttpEditor } from '../http-editor';
import { formatBytes } from '../../utils/formatBytes';
import { formatStreamingMessageTime } from './format-streaming-message-time';
import { StreamingMessageDirectionIcon } from './StreamingMessageDirectionIcon';
import {
  resolveStreamingMessageDataType,
  tryFormatJson,
} from './try-format-json';
import type {
  StreamingMessageDataType,
  StreamingMessageRow,
} from './streaming-messages.types';

function displayDataType({
  message,
  data,
}: {
  message: StreamingMessageRow;
  data?: string;
}): StreamingMessageDataType {
  if (message.dataType === 'binary') {
    return 'binary';
  }

  if (data !== undefined && data !== message.data) {
    return resolveStreamingMessageDataType({
      isBinary: false,
      data,
    });
  }

  return message.dataType;
}

export type StreamingMessagesPanelProps = {
  messages: StreamingMessageRow[];
  selectedMessageId?: string | null;
  onSelectMessage?: (message: StreamingMessageRow) => void;
  resolveMessageData?: (
    message: StreamingMessageRow,
  ) => Promise<string> | string;
  toolbarEnd?: ReactNode;
  detailHeaderEnd?: ReactNode;
  showToolbar?: boolean;
  getRowStyle?: (params: {
    data?: StreamingMessageRow;
  }) => CellStyle | undefined;
  onCellContextMenu?: (event: {
    data?: StreamingMessageRow;
    event?: Event | null;
  }) => void;
  height?: string;
};

export function StreamingMessagesPanel({
  messages,
  selectedMessageId,
  onSelectMessage,
  resolveMessageData,
  toolbarEnd,
  detailHeaderEnd,
  showToolbar = true,
  getRowStyle,
  onCellContextMenu,
  height = '360px',
}: StreamingMessagesPanelProps) {
  const tTable = useTranslations('designSystem.applicationAssets.table');
  const tWs = useTranslations('designSystem.chat.websocketClient');
  const tSse = useTranslations('designSystem.chat.sseClient');
  const tSections = useTranslations('designSystem.applicationAssets.sections');
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(
    null,
  );
  const [resolvedData, setResolvedData] = useState<{
    id: string;
    data: string;
  } | null>(null);

  const isControlled = selectedMessageId !== undefined;
  const selectedId = isControlled ? selectedMessageId : internalSelectedId;
  const selectedMessage = useMemo(
    () => messages.find((message) => message.id === selectedId),
    [messages, selectedId],
  );
  const selectedDisplayType = selectedMessage
    ? displayDataType({
        message: selectedMessage,
        data:
          resolvedData?.id === selectedMessage.id
            ? resolvedData.data
            : undefined,
      })
    : undefined;

  useEffect(() => {
    if (isControlled || messages.length === 0) {
      return;
    }

    const stillVisible = messages.some(
      (message) => message.id === internalSelectedId,
    );

    if (!stillVisible) {
      setInternalSelectedId(messages[0]?.id ?? null);
    }
  }, [isControlled, internalSelectedId, messages]);

  useEffect(() => {
    if (!selectedMessage) {
      setResolvedData(null);

      return;
    }

    if (!resolveMessageData) {
      setResolvedData({ id: selectedMessage.id, data: selectedMessage.data });

      return;
    }

    const result = resolveMessageData(selectedMessage);

    if (typeof result === 'string') {
      setResolvedData({ id: selectedMessage.id, data: result });

      return;
    }

    setResolvedData({ id: selectedMessage.id, data: selectedMessage.data });
    let cancelled = false;

    result
      .then((data) => {
        if (!cancelled) {
          setResolvedData({ id: selectedMessage.id, data });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedData({
            id: selectedMessage.id,
            data: selectedMessage.data,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolveMessageData, selectedMessage]);

  const handleSelect = useCallback(
    (message: StreamingMessageRow) => {
      if (!isControlled) {
        setInternalSelectedId(message.id);
      }

      onSelectMessage?.(message);
    },
    [isControlled, onSelectMessage],
  );

  const columnDefs = useMemo<ColDef<StreamingMessageRow>[]>(
    () => [
      {
        field: 'sequentialId',
        headerName: '#',
        width: 50,
        sortable: true,
      },
      {
        field: 'direction',
        headerName: '',
        width: 40,
        cellRenderer: (params: ICellRendererParams<StreamingMessageRow>) =>
          params.value ? (
            <StreamingMessageDirectionIcon direction={params.value} />
          ) : null,
      },
      {
        field: 'dataType',
        headerName: tTable('type'),
        width: 60,
        cellRenderer: (params: ICellRendererParams<StreamingMessageRow>) => {
          if (!params.data) {
            return null;
          }

          const dataType = displayDataType({ message: params.data });

          return (
            <Badge size="sm" variant="outline">
              {dataType}
            </Badge>
          );
        },
      },
      {
        field: 'dataPreview',
        headerName: tSse('data'),
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: ICellRendererParams<StreamingMessageRow>) => (
          <Text fontSize="xs" truncate>
            {params.value}
          </Text>
        ),
      },
      {
        field: 'dataSize',
        headerName: tTable('size'),
        width: 70,
        valueFormatter: (params) =>
          typeof params.value === 'number' ? formatBytes(params.value) : '',
      },
      {
        field: 'timestamp',
        headerName: tTable('time'),
        width: 80,
        valueFormatter: (params) => formatStreamingMessageTime(params.value),
      },
    ],
    [tSse, tTable],
  );

  const editorValue = useMemo(() => {
    const rawData =
      resolvedData && resolvedData.id === selectedMessage?.id
        ? resolvedData.data
        : (selectedMessage?.data ?? '');

    return tryFormatJson(rawData) ?? rawData;
  }, [resolvedData, selectedMessage]);

  return (
    <VStack gap="0" h={height} w="full" alignItems="stretch" overflow="hidden">
      {showToolbar ? (
        <HStack
          w="full"
          px="2"
          py="1"
          borderBottomWidth="1px"
          borderColor="border.default"
          bg="bg.subtle"
          gap="1"
          alignItems="center"
          flexShrink={0}
        >
          <Text fontSize="xs" fontWeight="medium">
            {tSections('websocketMessages')}
          </Text>
          <Badge size="sm" variant="outline">
            {messages.length}
          </Badge>
          <Box flex="1" />
          {toolbarEnd}
        </HStack>
      ) : null}

      {messages.length === 0 ? (
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="fg.muted" fontSize="xs">
            {tWs('noMessages')}
          </Text>
        </Box>
      ) : (
        <Splitter.Root
          orientation="horizontal"
          h="100%"
          w="full"
          defaultSize={[60, 40]}
          panels={[{ id: 'message-list' }, { id: 'message-detail' }]}
          borderWidth="0"
          gap={0}
        >
          <Splitter.Panel id="message-list" borderWidth="0" h="full" w="full">
            <Box h="full" w="full">
              <Table
                columnDefs={columnDefs}
                rowData={messages}
                getRowId={(params: { data: StreamingMessageRow }) =>
                  params.data.id
                }
                rowSelection="single"
                onRowClicked={(event: { data?: StreamingMessageRow }) => {
                  if (event.data) {
                    handleSelect(event.data);
                  }
                }}
                onCellContextMenu={onCellContextMenu}
                getRowStyle={getRowStyle}
                rowHeight={28}
                headerHeight={24}
                noBorder
                fontSize={11}
                headerFontSize={10}
                animateRows={false}
                defaultColDef={{
                  sortable: false,
                  filter: false,
                  resizable: true,
                }}
                suppressCellFocus
                hideColumnControls
              />
            </Box>
          </Splitter.Panel>

          <Splitter.ResizeTrigger
            id="message-list:message-detail"
            width="1px"
            minWidth="1px"
            bg="border.subtle"
            cursor="col-resize"
            _hover={{ bg: 'border.default' }}
          />

          <Splitter.Panel id="message-detail" borderWidth="0" h="full" w="full">
            <VStack
              h="full"
              w="full"
              gap="0"
              alignItems="stretch"
              overflow="hidden"
            >
              <HStack
                w="full"
                px="2"
                py="1"
                borderBottomWidth="1px"
                borderColor="border.default"
                bg="bg.subtle"
                gap="1"
                alignItems="center"
                flexShrink={0}
              >
                <Text fontSize="xs" fontWeight="medium">
                  {tSse('data')}
                </Text>
                {selectedMessage ? (
                  <>
                    <StreamingMessageDirectionIcon
                      direction={selectedMessage.direction}
                    />
                    <Badge size="sm" variant="outline">
                      {selectedDisplayType}
                    </Badge>
                    <Badge size="sm" variant="outline">
                      {formatBytes(selectedMessage.dataSize)}
                    </Badge>
                  </>
                ) : null}
                <Box flex="1" />
                {detailHeaderEnd}
              </HStack>
              <Box flex="1" w="full" overflow="hidden">
                {selectedMessage ? (
                  <HttpEditor
                    value={editorValue}
                    height="100%"
                    readOnly
                    showViewModeToggle
                    showStatusBar={false}
                  />
                ) : (
                  <Box
                    h="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color="fg.muted" fontSize="xs">
                      {tWs('noData')}
                    </Text>
                  </Box>
                )}
              </Box>
            </VStack>
          </Splitter.Panel>
        </Splitter.Root>
      )}
    </VStack>
  );
}
